// src/odoo/customers/components/CustomerDocumentsSection.tsx
// All comments are in English.

import React, { useState, useMemo, useEffect } from "react";
import type { DocumentsResponse, InvoiceDocument } from "../api/documentsApi";
import { formatDate, formatHKD } from "./utils";

interface Props {
  data?: DocumentsResponse;
  isLoading: boolean;
  error?: unknown;
}

const ITEMS_PER_PAGE = 10;

// Odoo web base url for opening the customer form
const ODOO_WEB_BASE_URL = import.meta.env.VITE_ODOO_WEB_URL;

export const CustomerDocumentsSection: React.FC<Props> = ({
  data,
  isLoading,
  error,
}) => {
  const [activeTab, setActiveTab] = useState<"sales_orders" | "invoices">(
    "sales_orders"
  );

  // Sorting state for sales orders
  const [soSortField, setSoSortField] = useState<string>("order_date");
  const [soSortDir, setSoSortDir] = useState<"asc" | "desc">("desc");

  // Sorting state for invoices
  const [invSortField, setInvSortField] = useState<string>("invoice_date");
  const [invSortDir, setInvSortDir] = useState<"asc" | "desc">("desc");

  // Paging state for sales orders
  const [soPage, setSoPage] = useState<number>(0);

  // Paging state for invoices
  const [invPage, setInvPage] = useState<number>(0);

  // Toggle sort helper
  const toggleSort = (
    field: string,
    currentField: string,
    setField: (f: string) => void,
    currentDir: "asc" | "desc",
    setDir: (d: "asc" | "desc") => void
  ) => {
    if (field === currentField) {
      setDir(currentDir === "asc" ? "desc" : "asc");
    } else {
      setField(field);
      setDir("asc");
    }
  };

  // Render sorting arrow icon
  const renderSortIcon = (
    field: string,
    activeField: string,
    dir: "asc" | "desc"
  ) => {
    if (field !== activeField) {
      // Neutral double arrow when the column is not the active sort
      return <span className="opacity-40 ml-1">↕</span>;
    }
    return <span className="ml-1">{dir === "asc" ? "▲" : "▼"}</span>;
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-4 shadow-sm h-[260px] animate-pulse" />
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        Failed to load documents.
      </div>
    );
  }

  if (!data) return null;

  const { sales_orders, invoices } = data;

  // Reset page when sorting or data length changes
  useEffect(() => {
    setSoPage(0);
  }, [soSortField, soSortDir, sales_orders.length]);

  useEffect(() => {
    setInvPage(0);
  }, [invSortField, invSortDir, invoices.length]);

  // Helper to parse ISO date strings safely
  const parseDate = (value: string | null | undefined): Date | null => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  };

  // Decide if invoice is overdue.
  // Prefer backend flags (is_overdue + residual) and fallback to local logic.
  const isInvoiceOverdue = (inv: InvoiceDocument): boolean => {
    const residual =
      typeof inv.residual === "number" ? inv.residual : Number(inv.residual ?? 0);

    // 1) Backend flag
    if (typeof inv.is_overdue === "boolean") {
      return inv.is_overdue && residual > 0;
    }

    // 2) Fallback: use due date + payment_state
    const due = parseDate(inv.invoice_date_due ?? inv.due_date ?? null);
    if (!due) return false;

    const paymentState = (inv.payment_state ?? "").toLowerCase();
    const isPaidLike =
      paymentState === "paid" ||
      paymentState === "in_payment" ||
      paymentState === "reversed" ||
      paymentState === "cancelled";

    if (isPaidLike || residual <= 0) return false;

    const today = new Date();
    return due < today;
  };

  // ----- Sales orders sorting & paging -----

  const sortedSalesOrders = useMemo(() => {
    const rows = [...sales_orders];

    rows.sort((a, b) => {
      const dirFactor = soSortDir === "asc" ? 1 : -1;

      const getVal = (row: (typeof sales_orders)[number]): string | number => {
        switch (soSortField) {
          case "order_name":
            return row.order_name ?? "";
          case "order_date":
            return row.order_date ?? "";
          case "order_date_due":
            return row.order_date_due ?? "";
          case "amount_total":
            return row.amount_total ?? 0;
          case "state":
          default:
            return row.state ?? "";
        }
      };

      const va = getVal(a);
      const vb = getVal(b);

      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * dirFactor;
      }

      const sa = String(va);
      const sb = String(vb);
      if (sa === sb) return 0;
      return sa < sb ? -1 * dirFactor : 1 * dirFactor;
    });

    return rows;
  }, [sales_orders, soSortField, soSortDir]);

  const soPageCount = Math.max(
    1,
    Math.ceil(sortedSalesOrders.length / ITEMS_PER_PAGE)
  );
  const soStart = soPage * ITEMS_PER_PAGE;
  const soEnd = soStart + ITEMS_PER_PAGE;
  const pagedSalesOrders = sortedSalesOrders.slice(soStart, soEnd);

  // ----- Invoices sorting & paging -----

  const sortedInvoices = useMemo(() => {
    const rows = [...invoices];

    rows.sort((a, b) => {
      const dirFactor = invSortDir === "asc" ? 1 : -1;

      const getVal = (row: InvoiceDocument): string | number => {
        switch (invSortField) {
          case "invoice_name":
            return row.invoice_name ?? "";
          case "invoice_date":
            return row.invoice_date ?? "";
          case "due_date":
            return row.invoice_date_due ?? row.due_date ?? "";
          case "amount_total":
            return row.amount_total ?? 0;
          case "residual":
            return row.residual ?? 0;
          case "payment_state":
          default:
            return (row.payment_state ?? row.state ?? "") as string;
        }
      };

      const va = getVal(a);
      const vb = getVal(b);

      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * dirFactor;
      }

      const sa = String(va);
      const sb = String(vb);
      if (sa === sb) return 0;
      return sa < sb ? -1 * dirFactor : 1 * dirFactor;
    });

    return rows;
  }, [invoices, invSortField, invSortDir]);

  const invPageCount = Math.max(
    1,
    Math.ceil(sortedInvoices.length / ITEMS_PER_PAGE)
  );
  const invStart = invPage * ITEMS_PER_PAGE;
  const invEnd = invStart + ITEMS_PER_PAGE;
  const pagedInvoices = sortedInvoices.slice(invStart, invEnd);

  // Handlers for opening documents in Odoo
  const handleOpenOrder = (orderId: number) => {
    const url = `${ODOO_WEB_BASE_URL}#id=${orderId}&model=sale.order&view_type=form`;
    window.open(url, "_blank");
  };

  const handleOpenInvoice = (invoiceId: number) => {
    const url = `${ODOO_WEB_BASE_URL}#id=${invoiceId}&cids=2-1-3&menu_id=121&action=253&model=account.move&view_type=form`;
    window.open(url, "_blank");
  };

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm h-[260px] flex flex-col">
      {/* Tabs header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-4 text-sm font-medium">
          <button
            type="button"
            className={`pb-1 border-b-2 ${
              activeTab === "sales_orders"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground"
            }`}
            onClick={() => setActiveTab("sales_orders")}
          >
            Sales orders ({sales_orders.length})
          </button>
          <button
            type="button"
            className={`pb-1 border-b-2 ${
              activeTab === "invoices"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground"
            }`}
            onClick={() => setActiveTab("invoices")}
          >
            Invoices ({invoices.length})
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab === "sales_orders" ? (
          <div className="h-full flex flex-col">
            {/* Sales orders table */}
            <div className="flex-1 overflow-auto">
              {sales_orders.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-4">
                  No sales orders for the current filters.
                </p>
              ) : (
                <table className="min-w-full text-xs">
                  <thead className="bg-muted/60">
                    <tr>
                      <th
                        className="px-2 py-1 text-left cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "order_name",
                            soSortField,
                            setSoSortField,
                            soSortDir,
                            setSoSortDir
                          )
                        }
                      >
                        Order
                        {renderSortIcon("order_name", soSortField, soSortDir)}
                      </th>
                      <th
                        className="px-2 py-1 text-left cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "order_date",
                            soSortField,
                            setSoSortField,
                            soSortDir,
                            setSoSortDir
                          )
                        }
                      >
                        Date
                        {renderSortIcon("order_date", soSortField, soSortDir)}
                      </th>
                      <th
                        className="px-2 py-1 text-left cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "order_date_due",
                            soSortField,
                            setSoSortField,
                            soSortDir,
                            setSoSortDir
                          )
                        }
                      >
                        Due date
                        {renderSortIcon(
                          "order_date_due",
                          soSortField,
                          soSortDir
                        )}
                      </th>
                      <th
                        className="px-2 py-1 text-right cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "amount_total",
                            soSortField,
                            setSoSortField,
                            soSortDir,
                            setSoSortDir
                          )
                        }
                      >
                        Amount
                        {renderSortIcon(
                          "amount_total",
                          soSortField,
                          soSortDir
                        )}
                      </th>
                      <th
                        className="px-2 py-1 text-left cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "state",
                            soSortField,
                            setSoSortField,
                            soSortDir,
                            setSoSortDir
                          )
                        }
                      >
                        Status
                        {renderSortIcon("state", soSortField, soSortDir)}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedSalesOrders.map((so) => (
                      <tr
                        key={so.order_id}
                        className="border-t hover:bg-muted/40 cursor-pointer"
                        onClick={() => handleOpenOrder(so.order_id)}
                      >
                        <td className="px-2 py-1 whitespace-nowrap">
                          {so.order_name}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          {formatDate(so.order_date)}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          {formatDate(so.order_date_due)}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap text-right">
                          {formatHKD(so.amount_total)}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          {so.state}
                          {so.is_overdue && so.overdue_days != null && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                              Overdue {so.overdue_days}d
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Sales orders pagination */}
            {sales_orders.length > ITEMS_PER_PAGE && (
              <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>
                  Page {soPage + 1} / {soPageCount}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="border rounded px-2 py-0.5 disabled:opacity-50"
                    onClick={() => setSoPage((p) => Math.max(0, p - 1))}
                    disabled={soPage === 0}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="border rounded px-2 py-0.5 disabled:opacity-50"
                    onClick={() =>
                      setSoPage((p) => Math.min(soPageCount - 1, p + 1))
                    }
                    disabled={soPage >= soPageCount - 1}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="h-full flex flex-col">
            {/* Invoices table */}
            <div className="flex-1 overflow-auto">
              {invoices.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-4">
                  No invoices for the current filters.
                </p>
              ) : (
                <table className="min-w-full text-xs">
                  <thead className="bg-muted/60">
                    <tr>
                      <th
                        className="px-2 py-1 text-left cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "invoice_name",
                            invSortField,
                            setInvSortField,
                            invSortDir,
                            setInvSortDir
                          )
                        }
                      >
                        Invoice
                        {renderSortIcon(
                          "invoice_name",
                          invSortField,
                          invSortDir
                        )}
                      </th>
                      <th
                        className="px-2 py-1 text-left cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "invoice_date",
                            invSortField,
                            setInvSortField,
                            invSortDir,
                            setInvSortDir
                          )
                        }
                      >
                        Date
                        {renderSortIcon(
                          "invoice_date",
                          invSortField,
                          invSortDir
                        )}
                      </th>
                      <th
                        className="px-2 py-1 text-left cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "due_date",
                            invSortField,
                            setInvSortField,
                            invSortDir,
                            setInvSortDir
                          )
                        }
                      >
                        Due date
                        {renderSortIcon("due_date", invSortField, invSortDir)}
                      </th>
                      <th
                        className="px-2 py-1 text-right cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "amount_total",
                            invSortField,
                            setInvSortField,
                            invSortDir,
                            setInvSortDir
                          )
                        }
                      >
                        Amount
                        {renderSortIcon(
                          "amount_total",
                          invSortField,
                          invSortDir
                        )}
                      </th>
                      <th
                        className="px-2 py-1 text-right cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "residual",
                            invSortField,
                            setInvSortField,
                            invSortDir,
                            setInvSortDir
                          )
                        }
                      >
                        Balance
                        {renderSortIcon("residual", invSortField, invSortDir)}
                      </th>
                      <th
                        className="px-2 py-1 text-left cursor-pointer select-none"
                        onClick={() =>
                          toggleSort(
                            "payment_state",
                            invSortField,
                            setInvSortField,
                            invSortDir,
                            setInvSortDir
                          )
                        }
                      >
                        Status
                        {renderSortIcon(
                          "payment_state",
                          invSortField,
                          invSortDir
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedInvoices.map((inv) => {
                      const residual =
                        typeof inv.residual === "number"
                          ? inv.residual
                          : Number(inv.residual ?? 0);
                      const overdue = isInvoiceOverdue(inv);

                      return (
                        <tr
                          key={inv.invoice_id}
                          className="border-t hover:bg-muted/40 cursor-pointer"
                          onClick={() => handleOpenInvoice(inv.invoice_id)}
                        >
                          <td className="px-2 py-1 whitespace-nowrap">
                            {inv.invoice_name}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap">
                            {formatDate(inv.invoice_date)}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap">
                            {formatDate(
                              inv.invoice_date_due ?? inv.due_date ?? null
                            )}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-right">
                            {formatHKD(inv.amount_total)}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-right">
                            {residual > 0 ? formatHKD(residual) : "-"}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap">
                            <span>
                              {(inv.payment_state || inv.state || "unknown")
                                .toString()
                                .toUpperCase()}
                            </span>
                            {overdue && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                                Overdue
                                {inv.overdue_days != null &&
                                  ` ${inv.overdue_days}d`}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Invoices pagination */}
            {invoices.length > ITEMS_PER_PAGE && (
              <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>
                  Page {invPage + 1} / {invPageCount}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="border rounded px-2 py-0.5 disabled:opacity-50"
                    onClick={() => setInvPage((p) => Math.max(0, p - 1))}
                    disabled={invPage === 0}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="border rounded px-2 py-0.5 disabled:opacity-50"
                    onClick={() =>
                      setInvPage((p) => Math.min(invPageCount - 1, p + 1))
                    }
                    disabled={invPage >= invPageCount - 1}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDocumentsSection;
